import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Divider,
  Stack,
  Link,
} from '@mui/material';
import type {
  PurchaseOrderCreateInput,
  PaymentTerms,
  ShipMethods,
} from '@repo/api-contract';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { gridPaymentItemProps } from './constants';
import {
  gridItemProps,
  textFieldProps,
  textFieldSelectProps,
} from '../../constants/form';
import dayjs, { Dayjs } from 'dayjs';
import { FormikProps } from 'formik';
import { getFormikField } from '../../../../util/fornik-helpers';
import { useTranslation } from 'react-i18next';
import { useSelector } from '../../../../state';
import { PurchaseOrderModalMode } from '../../../../state/purchaseOrderModalSlice';

export default function PurchaseOrderDetails({
  formik,
  paymentTerms,
  shipMethods,
  viewMode,
}: {
  formik: FormikProps<PurchaseOrderCreateInput>;
  paymentTerms: PaymentTerms[];
  shipMethods: ShipMethods[];
  viewMode: boolean;
}): JSX.Element {
  const { t: tGC } = useTranslation([], { keyPrefix: 'team.Global.Common' });
  const { t: tAddO } = useTranslation([], {
    keyPrefix: 'team.Modals.Purchase Orders.createAndEdit.Add Purchase Order',
  });

  const { mode } = useSelector(
    ({ purchaseOrderModalSlice }) => purchaseOrderModalSlice,
  );

  const orderDate = formik.values.orderDate
    ? dayjs.unix(formik.values.orderDate)
    : null;

  const handleChangeOrderDate = (newValue: Dayjs | null) => {
    // Convert the new date value to Unix time (seconds since the Unix Epoch)
    const unixTime = newValue ? newValue.unix() : null;

    // Update Formik's state with Unix time
    formik.setFieldValue('orderDate', unixTime);
  };

  const estimatedShipDate = formik.values.estimatedShipDate
    ? dayjs.unix(formik.values.estimatedShipDate)
    : null;

  const handleChangeEstShipDate = (newValue: Dayjs | null) => {
    const unixTime = newValue ? newValue.unix() : null;
    formik.setFieldValue('estimatedShipDate', unixTime);
  };

  const deadline = formik.values.deadline
    ? dayjs.unix(formik.values.deadline)
    : null;

  const handleChangeDeadline = (newValue: Dayjs | null) => {
    const unixTime = newValue ? newValue.unix() : null;
    formik.setFieldValue('deadline', unixTime);
  };

  const inHandsDate =
    formik.values.inHandsDate != 0
      ? dayjs.unix(formik.values.inHandsDate)
      : null;

  const handleChangeInHandsDate = (newValue: Dayjs | null) => {
    const unixTime = newValue ? newValue.unix() : null;
    formik.setFieldValue('inHandsDate', unixTime);
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('code', event.target.value);
  };

  const selectedPaymentTerm = getFormikField(formik, 'paymentTerms').value;
  const selectedShipMethod = getFormikField(formik, 'shipMethod').value;

  return (
    <>
      <Typography color="primary">{tAddO('PURCHASE ORDER DETAILS')}</Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
        sx={{ marginY: 2 }}
      >
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <Box {...textFieldProps}>
              {/* Disabled until quote module is implemented */}
              <Link
                color="text.secondary"
                underline="always"
                sx={{ cursor: 'pointer' }}
              >
                {tAddO('Import Estimate')}
              </Link>
            </Box>
          </Grid>
        </Stack>
        <Stack gap={2} width={'100%'}></Stack>
      </Stack>
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={viewMode || mode == PurchaseOrderModalMode.EDIT}
              {...textFieldProps}
              label={tAddO('Purchase Order No.')}
              name="code"
              type="number"
              error={Boolean(getFormikField(formik, 'code').error)}
              helperText={getFormikField(formik, 'code').error}
              value={getFormikField(formik, 'code').value}
              onChange={handleCodeChange}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={viewMode}
                label={tAddO('Purchase Order Date')}
                {...textFieldProps}
                value={orderDate}
                onChange={handleChangeOrderDate}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    error: Boolean(getFormikField(formik, 'orderDate').error),
                    helperText: getFormikField(formik, 'orderDate').error,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={viewMode}
              {...textFieldProps}
              label={tAddO('Vendor Account Number')}
              name="vendorAccountNumber"
              type="text"
              error={Boolean(
                getFormikField(formik, 'vendorAccountNumber').error,
              )}
              helperText={getFormikField(formik, 'vendorAccountNumber').error}
              value={getFormikField(formik, 'vendorAccountNumber').value}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              required
              disabled={viewMode}
              {...textFieldProps}
              label={tAddO('Vendor Name')}
              name="vendorName"
              type="text"
              error={Boolean(getFormikField(formik, 'vendorName').error)}
              helperText={getFormikField(formik, 'vendorName').error}
              value={getFormikField(formik, 'vendorName').value}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={viewMode}
              {...textFieldProps}
              label={tAddO('Vendor Address')}
              name="vendorAddress"
              type="text"
              error={Boolean(getFormikField(formik, 'vendorAddress').error)}
              helperText={getFormikField(formik, 'vendorAddress').error}
              value={getFormikField(formik, 'vendorAddress').value}
              onChange={formik.handleChange}
            />
          </Grid>
        </Stack>
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}>
          {/* DO not delete. This is other way to have dynamic tax rate with options to select.
                    <Grid {...gridPaymentItemProps}>
                            <Box {...textFieldProps}>
                            <Autocomplete
                                freeSolo
                                disableClearable
                                options={taxCodes}
                                onFocus={onNumberFieldFocus(formik, 'rate')}
                                onBlur={onNumberFieldBlur(formik, 'rate')}
                                // how to show options in dropdown
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return `${option.codeName} (${option.rate}%)`;
                                }}
                                // what’s currently in the input – comes from formik.rate
                                inputValue={
                                    getFormikField(formik, 'rate').value !== undefined &&
                                        getFormikField(formik, 'rate').value !== null
                                        ? String(getFormikField(formik, 'rate').value)
                                        : ''
                                }
                                // typing in the field → arbitrary custom rate
                                onInputChange={(_, newInputValue, reason) => {
                                    if (reason === 'input') {
                                        formik.setFieldValue(
                                            'rate',
                                            newInputValue === '' ? '' : Number(newInputValue)
                                        );
                                    }
                                }}
                                // selecting from dropdown → use selected tax code's rate
                                onChange={(_, newValue) => {
                                    if (newValue && typeof newValue !== 'string') {
                                        formik.setFieldValue('rate', newValue.rate);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        label={tMSST('Sales Tax Code')}
                                        name="rate"
                                        type="number"
                                        disabled={viewMode}
                                        error={Boolean(getFormikField(formik, 'rate').error)}
                                        helperText={getFormikField(formik, 'rate').error}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    <InputAdornment position="end">%</InputAdornment>
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Grid> */}
          <Grid {...gridPaymentItemProps}>
            <TextField
              variant="filled"
              {...textFieldProps}
              {...textFieldSelectProps}
              label={tAddO('Payment Terms')}
              select
              name="paymentTerms"
              disabled={viewMode}
              error={Boolean(getFormikField(formik, 'paymentTerms').error)}
              helperText={getFormikField(formik, 'paymentTerms').error}
              value={
                Array.isArray(selectedPaymentTerm) &&
                selectedPaymentTerm.length > 0
                  ? selectedPaymentTerm[0].PaymentTermId
                  : ''
              }
              onChange={(event) => {
                const selectedTerm = paymentTerms.find(
                  (pT) => pT.PaymentTermId === event.target.value,
                );
                formik.setFieldValue(
                  'paymentTerms',
                  selectedTerm ? [selectedTerm] : [],
                );
              }}
            >
              <MenuItem value={''}>{tGC('None')}</MenuItem>
              {paymentTerms.map((pT) => {
                return (
                  <MenuItem key={pT.PaymentTermId} value={pT.PaymentTermId}>
                    {pT.name}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={viewMode}
              {...textFieldProps}
              label={tAddO('Ship To Name')}
              name="shipToName"
              type="text"
              error={Boolean(getFormikField(formik, 'shipToName').error)}
              helperText={getFormikField(formik, 'shipToName').error}
              value={getFormikField(formik, 'shipToName').value}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={viewMode}
              {...textFieldProps}
              label={tAddO('Ship To Address')}
              name="shipToAddress"
              type="text"
              error={Boolean(getFormikField(formik, 'shipToAddress').error)}
              helperText={getFormikField(formik, 'shipToAddress').error}
              value={getFormikField(formik, 'shipToAddress').value}
              onChange={formik.handleChange}
            />
          </Grid>
        </Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={viewMode}
                label={tAddO('Estimated Ship Date')}
                {...textFieldProps}
                value={estimatedShipDate}
                onChange={handleChangeEstShipDate}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    error: Boolean(
                      getFormikField(formik, 'estimatedShipDate').error,
                    ),
                    helperText: getFormikField(formik, 'estimatedShipDate')
                      .error,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid {...gridPaymentItemProps}>
            <TextField
              variant="filled"
              {...textFieldProps}
              {...textFieldSelectProps}
              label={tAddO('Ship Method')}
              select
              name="shipMethod"
              disabled={viewMode}
              error={Boolean(getFormikField(formik, 'shipMethod').error)}
              helperText={getFormikField(formik, 'shipMethod').error}
              value={selectedShipMethod}
              onChange={(event) => {
                const selectedSM = shipMethods.find(
                  (sM) => sM.ShipMethodId === event.target.value,
                );
                formik.setFieldValue(
                  'shipMethod',
                  selectedSM ? selectedSM.ShipMethodId : '',
                );
              }}
            >
              <MenuItem value={''}>{tGC('None')}</MenuItem>
              {shipMethods.map((sM) => {
                return (
                  <MenuItem key={sM.ShipMethodId} value={sM.ShipMethodId}>
                    {sM.method}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid {...gridItemProps}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={viewMode}
                label={tAddO('Deadline')}
                {...textFieldProps}
                value={deadline}
                onChange={handleChangeDeadline}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    error: Boolean(getFormikField(formik, 'deadline').error),
                    helperText: getFormikField(formik, 'deadline').error,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid {...gridItemProps}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={viewMode}
                label={tAddO('In Hands Date')}
                {...textFieldProps}
                value={inHandsDate}
                onChange={handleChangeInHandsDate}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    error: Boolean(getFormikField(formik, 'inHandsDate').error),
                    helperText: getFormikField(formik, 'inHandsDate').error,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Stack>
      </Stack>
    </>
  );
}
